import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import PayoffChart from "../../components/strategy/PayoffChart";
import TutorialStrategyTour from "../../components/backtest/TutorialStrategyTour";
import { STRATEGY_OPTIONS } from "../../constants/strategies";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
  Table,
  Spinner
} from "reactstrap";
import { useAccount } from "../../context/AccountContext";
import PageTitleAlt2 from "../../Layout/AppMain/PageTitleAlt2";
import {
  Modal,
  ModalHeader,
  ModalBody
} from "reactstrap";


// Quick-pick suggestions for FUTURES_PYRAMID_HEDGING — not an exhaustive
// whitelist. The strategy supports any symbol with liquid futures contracts
// (indices AND stocks); these are just the common index futures shown first.
const IFP_INDEX_SUGGESTIONS = ["NIFTY", "BANKNIFTY", "FINNIFTY", "MIDCPNIFTY"];

// FUTURES_PYRAMID_HEDGING manages a futures position, not a cash equity
// holding — read quantity/avg price from the snapshot's "futures" leg
// (overlay_futures_positions in build_positions_snapshot.py) instead of
// "stock", or build payloads end up anchored to the wrong instrument.
const getPositionQty = (stock, strategyType) =>
  strategyType === "FUTURES_PYRAMID_HEDGING" ? (stock.futures?.quantity || 0) : (stock.stock?.quantity || 0);
const getPositionPrice = (stock, strategyType) =>
  strategyType === "FUTURES_PYRAMID_HEDGING" ? (stock.futures?.avg_price || 0) : (stock.stock?.avg_price || 0);

// Advanced Settings fields render in whatever key order the DB's
// user_settings JSON happens to have, which varies per account.
// Force specific pairs to render adjacently regardless of that order.
const ADVANCED_SETTINGS_ADJACENT_PAIRS = [["csp_enabled", "min_put_distance_percent"]];

const reorderAdjacentSettings = (entries, pairs) => {
  const result = [...entries];
  pairs.forEach(([afterKey, moveKey]) => {
    const moveIdx = result.findIndex(([k]) => k === moveKey);
    if (moveIdx === -1) return;
    const [moved] = result.splice(moveIdx, 1);
    const afterIdx = result.findIndex(([k]) => k === afterKey);
    if (afterIdx === -1) {
      result.splice(moveIdx, 0, moved);
      return;
    }
    result.splice(afterIdx + 1, 0, moved);
  });
  return result;
};

const StrategyBuild = () => {
  const { selectedAccount } = useAccount();
  const username = selectedAccount;
  const MAX_CONCURRENT_BUILDS = 1;

  const [error, setError] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [loadingStocks, setLoadingStocks] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [detailsModal, setDetailsModal] =
  useState(false);

  const [selectedResult, setSelectedResult] =
  useState(null);
  const [selectedResultStrategyType, setSelectedResultStrategyType] =
  useState(null);
  const structure = selectedResult?.derived;

  // saved strategies waiting for activation
  const [savedStrategies, setSavedStrategies] = useState([]);
  const [activatingId, setActivatingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [capitalPlannerOpen, setCapitalPlannerOpen] = useState(false);
  const [activationModalOpen, setActivationModalOpen] = useState(false);




  // Pre-fill symbol and strategy type from URL params (set by Manage Strategy button)
  const urlSymbol       = new URLSearchParams(window.location.search).get("symbol") || "";
  const urlStrategyType = new URLSearchParams(window.location.search).get("strategy_type") || "";

  const [manualTicker, setManualTicker] = useState(urlSymbol);
  const [strategyType, setStrategyType] = useState(urlStrategyType || "PYRAMID_HEDGING");
  const [availableExpiries, setAvailableExpiries] = useState([]);

  const buildButtonRef       = useRef(null);   // scroll target
  const savedSettingsApplied = useRef(false);  // prevent double-apply

  const [settingsSchema, setSettingsSchema] = useState(null);
  const [dynamicValues, setDynamicValues] = useState({});

  const [stocks, setStocks] = useState([]);
  const [previewReady, setPreviewReady] = useState(false);
  const [sortField, setSortField] = useState("lots");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir(field === "lots" ? "desc" : "asc");
    }
  };

  const sortedStocks = [...stocks].sort((a, b) => {
    const lotsA = (a.lot_size > 0) ? (a.stock?.quantity || 0) / a.lot_size : 0;
    const lotsB = (b.lot_size > 0) ? (b.stock?.quantity || 0) / b.lot_size : 0;
    let cmp = 0;
    if (sortField === "lots") {
      cmp = lotsA - lotsB;
    } else {
      cmp = a.stock_name.localeCompare(b.stock_name);
    }
    return sortDir === "asc" ? cmp : -cmp;
  });

  // When switching INTO FUTURES_PYRAMID_HEDGING, default to NIFTY if no symbol
  // is set yet (supports index or stock futures — not restricted to indices).
  useEffect(() => {
    if (urlSymbol) return; // don't interfere when arriving via Rebuild button
    if (strategyType === "FUTURES_PYRAMID_HEDGING") {
      setManualTicker(prev => prev || "NIFTY");
    } else if (strategyType === "WEEKLY_INDEX_SHORT_STRADDLE") {
      // SPX-only strategy — lock the symbol.
      setManualTicker("SPX");
    } else {
      // Returning to a stock-option strategy: clear an index symbol that was
      // only valid for futures, keep a stock symbol as-is.
      setManualTicker(prev => IFP_INDEX_SUGGESTIONS.includes(prev) ? "" : prev);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyType]);

  // Auto-fetch stocks when arriving via Rebuild Strategy link
  useEffect(() => {
    if (urlSymbol && selectedAccount) {
      addStockBySymbol(urlSymbol);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  // Auto-populate the table with any stock currently held (non-zero position)
  // or with an ACTIVE strategy (even if the stock leg was sold off, e.g. a
  // short call still open with no shares held), so it shows up without the
  // user having to type its ticker.
  // Skipped when arriving via Rebuild Strategy link, which already targets one symbol.
  useEffect(() => {
    if (urlSymbol || !selectedAccount) return;

    const loadExistingPositions = async () => {
      try {
        const [posRes, strategyRes] = await Promise.all([
          axios.get("/positions/filter", {
            params: { username: selectedAccount, stock_traded: true }
          }),
          axios.get("/strategy/view", { params: { username: selectedAccount } }).catch(() => ({ data: [] }))
        ]);
        const heldSymbols = (posRes.data.stocks || []).map(s => s.stock_name);
        const activeStrategySymbols = (strategyRes.data || [])
          .filter(s => s.status === "ACTIVE")
          .map(s => s.symbolname);
        const allSymbols = [...new Set([...heldSymbols, ...activeStrategySymbols])];
        for (const sym of allSymbols) {
          await addStockBySymbol(sym, { autoPopulated: true });
        }
      } catch (err) {
        console.error("Failed to load existing positions", err);
      }
    };

    loadExistingPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  // ------------------------------------------------
  // FETCH STRATEGY SETTINGS
  // ------------------------------------------------
  useEffect(() => {
    if (!strategyType || !selectedAccount) return;

    const fetchSettings = async () => {
      try {
        setLoadingSettings(true);

        const res = await axios.get("/strategy/settings", {
          params: {
            strategy_type: strategyType,
            username: selectedAccount
          }
        });

        setSettingsSchema(res.data);

  const initialValues = {};

  // variable inputs
  Object.entries(res.data.variable_inputs || {}).forEach(
    ([key, config]) => {
      initialValues[key] =
        config.default !== undefined
          ? config.default
          : "";
    }
  );

  // advanced user settings
  Object.entries(res.data.user_settings || {}).forEach(
    ([key, config]) => {
      initialValues[key] =
        config.value !== undefined
          ? config.value
          : "";
    }
  );

  setDynamicValues(initialValues);


      } catch {
        setError("Failed to load strategy settings.");
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchSettings();
  }, [strategyType, selectedAccount]);

  // ------------------------------------------------
  // FETCH AVAILABLE EXPIRIES
  // Re-fetches when strategyType changes: DHSS gets weekly expiries,
  // PH gets monthly-only expiries.
  // ------------------------------------------------
  useEffect(() => {
    axios.get("/expiries", { params: { strategy_type: strategyType, symbol: manualTicker } })
      .then(res => {
        const exp = res.data.expiries || [];
        setAvailableExpiries(exp);
        if (exp.length > 0) {
          setDynamicValues(prev => ({ ...prev, expiry: exp[0] }));
        }
      })
      .catch(() => setAvailableExpiries([]));
  }, [strategyType, manualTicker]);

  // ------------------------------------------------
  // APPLY SAVED SETTINGS FROM REBUILD BUTTON
  // Runs once both system settings and available expiries are ready,
  // overriding defaults with the strategy's own stored settings.
  // ------------------------------------------------
  useEffect(() => {
    if (!urlSymbol || savedSettingsApplied.current) return;
    if (!settingsSchema || availableExpiries.length === 0) return;

    const key    = `rebuild_settings_${urlSymbol}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) return;

    try {
      const parsed = JSON.parse(stored);
      sessionStorage.removeItem(key);
      savedSettingsApplied.current = true;

      setDynamicValues(prev => {
        const next = { ...prev };
        Object.entries(parsed).forEach(([k, v]) => {
          // Skip internal meta fields; skip expiry (keep the available-expiry dropdown value)
          if (k === "_variable_inputs" || k === "expiry") return;
          if (Object.prototype.hasOwnProperty.call(next, k)) {
            next[k] = v;
          }
        });
        // Apply expiry only if it's still a valid available expiry
        if (parsed.expiry && availableExpiries.includes(parsed.expiry)) {
          next.expiry = parsed.expiry;
        }
        return next;
      });
    } catch (e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSymbol, settingsSchema, availableExpiries]);

  // ------------------------------------------------
  // SCROLL TO BUILD STRATEGY BUTTON AFTER AUTO-FETCH
  // ------------------------------------------------
  useEffect(() => {
    if (urlSymbol && stocks.length > 0 && buildButtonRef.current) {
      buildButtonRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stocks.length]);

  // ------------------------------------------------
  // SAVE STRATEGIES
  // ------------------------------------------------
  const handleSaveStrategy = async () => {
    try {
      setError(null);
      setIsSaving(true);

      const strategiesToSave = stocks
        .filter(s => s.selected && s.status === "Success" && s.buildResult)
        .map(s => ({ symbolname: s.stock_name, strategy_state: s.buildResult }));

      if (strategiesToSave.length === 0) {
        setError("No successful strategies to save.");
        return;
      }

      const res = await axios.post("/strategy/save", {
        username,
        strategy_type: strategyType,
        strategies: strategiesToSave,
      });

      // Build the activate card directly from save response — no extra /strategy/view round-trip
      const resultMap = {};
      for (const r of (res.data.results || [])) {
        resultMap[r.symbolname] = r;
      }

      const isDhss = strategyType === "DELTA_HEDGED_SHORT_STRANGLE";

      const saved = strategiesToSave.map(s => {
        const r = resultMap[s.symbolname] || {};
        const wasActive = r.was_active || false;
        return {
          symbolname:   s.symbolname,
          strategy_id:  r.strategy_id,
          was_active:   wasActive,
          call_symbol:  s.strategy_state?.derived?.call_symbol || "—",
          put_symbol:   s.strategy_state?.derived?.put_symbol  || null,
          target_lots:  s.strategy_state?.derived?.target_lots,
          // DHSS INSERT already sets status=ACTIVE; no separate activation step needed
          activated:    isDhss && !wasActive,
          error:        null,
        };
      });

      setSavedStrategies(saved);
      setActivationModalOpen(true);

    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to save strategies");
    } finally {
      setIsSaving(false);
    }
  };

  const handleActivate = async (symbolname, strategyId) => {
    if (!strategyId) return;
    setActivatingId(strategyId);
    try {
      await axios.patch(`/strategy/${strategyId}/status`, { status: "ACTIVE" });
      setSavedStrategies(prev =>
        prev.map(s => s.symbolname === symbolname
          ? { ...s, status: "ACTIVE", activated: true, error: null }
          : s
        )
      );
    } catch (err) {
      setSavedStrategies(prev =>
        prev.map(s => s.symbolname === symbolname
          ? { ...s, error: err.response?.data?.error || "Activation failed" }
          : s
        )
      );
    } finally {
      setActivatingId(null);
    }
  };

  // ------------------------------------------------
  // ADD STOCK (looks up real position for this symbol, if any held)
  // ------------------------------------------------
  const addStockBySymbol = async (symRaw, { autoPopulated = false } = {}) => {
    const sym = (symRaw || "").trim().toUpperCase();
    if (!sym) return;
    if (!selectedAccount) {
      setError("No account selected.");
      return;
    }
    if (stocks.some(s => s.stock_name === sym)) {
      setManualTicker("");
      return;
    }

    try {
      setError(null);
      setLoadingStocks(true);

      const [response, strategyRes, optionPosRes] = await Promise.all([
        axios.post("/positions/filter", { username: selectedAccount, stock_name: sym }).catch(() => ({ data: { stocks: [] } })),
        axios.get("/strategy/view", { params: { username: selectedAccount } }).catch(() => ({ data: [] })),
        axios.get("/trading/option-positions", { params: { username: selectedAccount } }).catch(() => ({ data: [] }))
      ]);

      const matched = (response.data.stocks || [])[0] || null;

      // Build set of symbols with a live short CE in optionpositions (authoritative source)
      const liveShortCeSymbols = new Set();
      for (const op of (optionPosRes.data || [])) {
        if (op.optiontype === "CE" && (op.netqty || 0) < 0) {
          liveShortCeSymbols.add(op.symbolname);
        }
      }

      // active_call_symbol, only when a live short CE actually exists
      let activeCallSymbol = null;
      for (const s of (strategyRes.data || [])) {
        if (s.symbolname === sym && s.strategy_type === "PYRAMID_HEDGING") {
          const rt = s.strategy_json?.runtime || {};
          if (rt.call_hedge_placed && rt.active_call_symbol && liveShortCeSymbols.has(sym)) {
            activeCallSymbol = rt.active_call_symbol;
          }
        }
      }

      // Does this symbol already have an ACTIVE strategy of the type being built?
      // If so, default the checkbox unchecked so re-visiting the page (which
      // auto-populates every currently-held stock) doesn't silently re-select
      // stocks the user has no intention of rebuilding.
      const hasActiveStrategy = (strategyRes.data || []).some(
        s => s.symbolname === sym && s.strategy_type === strategyType && s.status === "ACTIVE"
      );

      // Per-symbol delta contributions from option positions (lot-normalised)
      let putAbsDelta = 0, callAbsDelta = 0;
      for (const op of (optionPosRes.data || [])) {
        if (op.symbolname !== sym) continue;
        const opLotSize = parseFloat(op.lotsize) || 1;
        const posDeltaLots = (op.netqty || 0) * (op.delta || 0) / opLotSize;
        if (op.optiontype === "PE" && (op.netqty || 0) < 0) {
          putAbsDelta += Math.abs(posDeltaLots);
        } else if (op.optiontype === "CE" && (op.netqty || 0) < 0) {
          callAbsDelta += Math.abs(posDeltaLots);
        }
      }

      const lotSize = matched?.lot_size || 100;
      const stockLots = (matched?.stock?.quantity || 0) / lotSize;

      const newRow = {
        stock_name: sym,
        lot_size: lotSize,
        underlying_ltp: matched?.underlying_ltp || matched?.stock?.ltp || 0,
        // Only default to unchecked when this row came from the passive
        // "auto-populate everything I currently hold" effect. Explicit adds
        // (manual ticker entry, or the Rebuild Strategy deep link) are a
        // deliberate action on that one symbol, so keep them checked.
        selected: !(autoPopulated && hasActiveStrategy),
        status: hasActiveStrategy ? "Active" : "Pending",
        buildResult: null,
        overrides: { ...dynamicValues },
        active_call_symbol: activeCallSymbol,
        bullish_delta: parseFloat((stockLots + putAbsDelta).toFixed(3)),
        bearish_delta: parseFloat(callAbsDelta.toFixed(3)),
        stock: matched?.stock || { quantity: 0, avg_price: 0, ltp: 0, delta: 0, buyprice: 0 },
      };

      setStocks(prev => [...prev, newRow]);
      setManualTicker("");

    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to add stock.");
    } finally {
      setLoadingStocks(false);
    }
  };

  const handleAddTicker = () => addStockBySymbol(manualTicker);


  const updateStockStatus = (name, status) => {
    setStocks(prev =>
      prev.map(s =>
        s.stock_name === name
          ? { ...s, status }
          : s
      )
    );
  };

  const updateStockResult = (
    name,
    status,
    result
  ) => {
    setStocks(prev =>
      prev.map(s =>
        s.stock_name === name
          ? {
              ...s,
              status,
              buildResult: result
            }
          : s
      )
    );
  };

  
  const buildSingleStrategy = async (stock) => {
    try {
      // Validate required fields before hitting the API
      const mergedInputs = { ...stock.overrides, ...dynamicValues };

      if (strategyType === "PYRAMID_HEDGING" || strategyType === "FUTURES_PYRAMID_HEDGING") {
        if (!mergedInputs.expiry) {
          updateStockResult(stock.stock_name, "Failed", {
            error: "Option Expiry is required. Select an expiry date in the form above before building.",
            suggestions: ["Choose an expiry from the Expiry dropdown and then click Build Strategy."]
          });
          return;
        }

        if (stock.active_call_symbol) {
          const proceed = window.confirm(
            `${stock.stock_name} has an active short call (${stock.active_call_symbol}).\n\n` +
            `You must square off the call before rebuilding the strategy with a new one.\n\n` +
            `Click OK only if you have already squared it off and want to proceed.`
          );
          if (!proceed) return;
        }
      }

      updateStockStatus(stock.stock_name, "Building");

      const payload = {
        username: selectedAccount,
        stock_name: stock.stock_name,
        quantity: getPositionQty(stock, strategyType),
        buyprice: getPositionPrice(stock, strategyType),
        underlying_ltp: stock.underlying_ltp || stock.stock?.ltp || 0,
        strategy_type: strategyType,
        variable_inputs: mergedInputs
      };

      const res = await axios.post(
        "/strategy/build-strategy-state",
        payload
      );

      if (res.data.status === "failed") {
        updateStockResult(stock.stock_name, "Failed", {
          error: res.data.error,
          suggestions: res.data.suggestions || []
        });
      } else {
        const buildResult = res.data.data;
        if (res.data.warning) buildResult.warning = res.data.warning;
        updateStockResult(stock.stock_name, "Success", buildResult);
      }

    } catch (err) {
      updateStockResult(stock.stock_name, "Failed", {
        error: err.response?.data?.error || err.response?.data?.message || "Unexpected error",
        suggestions: []
      });
    }
  };
  // ------------------------------------------------
  // PREVIEW (SHOW PAYLOAD ONLY)
  // ------------------------------------------------
  const handleBuildStrategy = async () => {

    const queue =
      stocks.filter(s => s.selected);

    let index = 0;

    const worker = async () => {
      while (index < queue.length) {

        const current =
          queue[index++];

        await buildSingleStrategy(
          current
        );
      }
    };

    await Promise.all(
      Array.from(
        { length: MAX_CONCURRENT_BUILDS },
        worker
      )
    );

    // ✅ enable save after build completes
    setPreviewReady(true);
  };


  // ------------------------------------------------
  // TABLE HANDLERS
  // ------------------------------------------------
  const toggleSelect = (stockName) => {
    setStocks(prev =>
      prev.map(s =>
        s.stock_name === stockName
          ? { ...s, selected: !s.selected }
          : s
      )
    );
  };

  const toggleSelectAll = (checked) => {
  setStocks(prev =>
    prev.map(s => ({
      ...s,
      selected: checked
    }))
  );
};

  const updateOverride = (stockName, key, value) => {
    setStocks(prev =>
      prev.map(s =>
        s.stock_name === stockName
          ? {
              ...s,
              overrides: {
                ...s.overrides,
                [key]: value
              }
            }
          : s
      )
    );
  };

  const isTourMode = new URLSearchParams(window.location.search).get("tour") === "build_strategy";

  return (
    <>
      {isTourMode && (
        <TutorialStrategyTour isOpen={isTourMode} />
      )}
      <PageTitleAlt2
        heading="Build Strategy"
        subheading="Configure and preview strategy parameters"
        icon="pe-7s-plus icon-gradient bg-happy-itmeo"
      />

      {/* ===================================================== */}
      {/* CARD 1 — FETCH STOCKS */}
      {/* ===================================================== */}
      <Card className="mt-3" data-tour="fetch-card">
        <CardHeader>Add Stock</CardHeader>
        <CardBody>
          {error && <Alert color="danger">{error}</Alert>}

          <Form>
            <Row>
              <Col md={4}>
                <FormGroup data-tour="strategy-type">
                  <Label>Strategy Type</Label>
                  <Input
                    type="select"
                    value={strategyType}
                    onChange={(e) =>
                      setStrategyType(e.target.value)
                    }
                  >
                    {STRATEGY_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label>{strategyType === "FUTURES_PYRAMID_HEDGING" ? "Symbol (Index or Stock)" : "Stock Name"}</Label>
                  {strategyType === "WEEKLY_INDEX_SHORT_STRADDLE" ? (
                    <Input type="text" value="SPX" disabled />
                  ) : strategyType === "FUTURES_PYRAMID_HEDGING" ? (
                    <>
                      <Input
                        type="text"
                        list="ifp-symbol-suggestions"
                        value={manualTicker}
                        onChange={e => setManualTicker(e.target.value.toUpperCase())}
                        onKeyDown={e => e.key === "Enter" && handleAddTicker()}
                        data-tour="manual-ticker"
                      />
                      <datalist id="ifp-symbol-suggestions">
                        {IFP_INDEX_SUGGESTIONS.map(s => <option key={s} value={s} />)}
                      </datalist>
                    </>
                  ) : (
                    <Input
                      type="text"
                      placeholder="e.g. NVDA"
                      value={manualTicker}
                      onChange={e => setManualTicker(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === "Enter" && handleAddTicker()}
                      data-tour="manual-ticker"
                    />
                  )}
                </FormGroup>
              </Col>
            </Row>

            {settingsSchema && (
              <Row>
                {Object.entries(settingsSchema.variable_inputs || {}).map(
                  ([key, config]) => (
                    <Col md={4} key={key}>
                      <FormGroup>
                        <Label>{config.title}</Label>
                        {key === "expiry" ? (
                          <Input
                            type="select"
                            value={dynamicValues[key] || ""}
                            onChange={(e) =>
                              setDynamicValues({
                                ...dynamicValues,
                                [key]: e.target.value
                              })
                            }
                          >
                            <option value="">— Select expiry —</option>
                            {availableExpiries.map(exp => (
                              <option key={exp} value={exp}>
                                {new Date(exp + "T00:00:00").toLocaleDateString("en-US", {
                                  day: "2-digit", month: "short", year: "numeric"
                                })}
                              </option>
                            ))}
                          </Input>
                        ) : config.type === "boolean" ? (
                          <Input
                            type="select"
                            value={String(dynamicValues[key] ?? "")}
                            onChange={(e) =>
                              setDynamicValues({
                                ...dynamicValues,
                                [key]: e.target.value === "true"
                              })
                            }
                          >
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </Input>
                        ) : (
                          <Input
                            type={config.type}
                            value={dynamicValues[key] || ""}
                            onChange={(e) =>
                              setDynamicValues({
                                ...dynamicValues,
                                [key]: e.target.value
                              })
                            }
                          />
                        )}
                      </FormGroup>
                    </Col>
                  )
                )}
              </Row>
            )}

            <div className="mt-3 d-flex align-items-center" style={{ gap: 8, flexWrap: "wrap" }}>
              <Button
                color="primary"
                onClick={handleAddTicker}
                disabled={loadingStocks || !manualTicker.trim()}
                data-tour="add-ticker"
              >
                {loadingStocks ? <Spinner size="sm" /> : "Add Stock"}
              </Button>
              {strategyType === "PYRAMID_HEDGING" && (
                <Button
                  color="link"
                  style={{ fontSize: 13 }}
                  onClick={() => { window.location.href = "/strategy/portfolio-builder"; }}
                >
                  Building many stocks at once? Try Portfolio Builder →
                </Button>
              )}
            </div>
          </Form>
        </CardBody>
      </Card>
      {/* ===================================================== */}
      {/* CARD 2 — STOCK TABLE */}
      {/* ===================================================== */}
      {stocks.length > 0 && (
        <Card className="mt-4" data-tour="stock-table">
          <CardHeader>Selected Stocks</CardHeader>
          <CardBody>
            <Table bordered hover responsive style={{ textAlign: "center" }}>
              <thead>
                <tr>
                  <th>
                    <Input
                      type="checkbox"
                      onChange={(e) =>
                        toggleSelectAll(e.target.checked)
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleSort("name")}
                    style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                  >
                    Stock {sortField === "name" ? (sortDir === "asc" ? "▲" : "▼") : <span style={{ color: "#ccc" }}>⇅</span>}
                  </th>
                  <th
                    onClick={() => handleSort("lots")}
                    style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                  >
                    Qty {sortField === "lots" ? (sortDir === "asc" ? "▲" : "▼") : <span style={{ color: "#ccc" }}>⇅</span>}
                  </th>
                  <th title="abs(Short Put Delta) + Stock Delta">Bullish Δ</th>
                  <th title="Net delta of short calls">Bearish Δ</th>
                  <th title="Avg % gap between adjacent ladder buy prices">Step Gap %</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>

                            <tbody>
                {sortedStocks.map(stock => (
                  <tr key={stock.stock_name}>
                    <td>
                      <Input
                        type="checkbox"
                        checked={stock.selected}
                        onChange={() =>
                          toggleSelect(stock.stock_name)
                        }
                      />
                    </td>

                    <td>
                      <a
                        href={`/trading/${stock.stock_name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontWeight: 500 }}
                      >
                        {stock.stock_name}
                      </a>
                    </td>

                    <td>
                      {getPositionQty(stock, strategyType) > 0 ? (
                        <>
                          {getPositionQty(stock, strategyType)}
                          {stock.lot_size > 0 && (
                            <span style={{ color: "#888", fontSize: "0.85em", marginLeft: 4 }}>
                              ({parseFloat((getPositionQty(stock, strategyType) / stock.lot_size).toFixed(2))} lots)
                            </span>
                          )}
                        </>
                      ) : <span style={{ color: "#aaa", fontSize: 12 }}>not purchased</span>}
                    </td>

                    <td>
                      {(() => {
                        const d = stock.buildResult?.derived;
                        if (d) {
                          const lotSize = d.lot_size || 1;
                          const stockLots = getPositionQty(stock, strategyType) / lotSize;
                          const putLots = d.opening_strangle_mode ? (d.target_lots || 1) : 1;
                          const putContrib = (d.csp_enabled && !d.csp_error && d.put_delta != null)
                            ? Math.abs(d.put_delta) * putLots
                            : 0;
                          const val = stockLots + putContrib;
                          return val > 0 ? parseFloat(val.toFixed(3)) : "—";
                        }
                        return stock.bullish_delta > 0 ? stock.bullish_delta : "—";
                      })()}
                    </td>

                    <td>
                      {(() => {
                        const d = stock.buildResult?.derived;
                        if (d && d.call_delta != null) {
                          const val = d.call_delta * (d.target_lots || 1);
                          return val > 0 ? parseFloat(val.toFixed(3)) : "—";
                        }
                        return stock.bearish_delta > 0 ? stock.bearish_delta : "—";
                      })()}
                    </td>

                    <td>
                      {(() => {
                        const ladder = stock.buildResult?.derived?.ladder;
                        if (!ladder || ladder.length < 2) return "—";
                        let total = 0;
                        for (let i = 0; i < ladder.length - 1; i++) {
                          total += Math.abs((ladder[i + 1].buy_price - ladder[i].buy_price) / ladder[i].buy_price * 100);
                        }
                        return (total / (ladder.length - 1)).toFixed(1) + "%";
                      })()}
                    </td>

                    <td>
                      {stock.status === "Building" && (
                        <Spinner size="sm" />
                      )}
                      {stock.status !== "Building" && (
                        <>
                          {stock.status === "Success" && stock.buildResult?.derived?.exceeds_target_price
                            ? (
                              <span
                                title={stock.buildResult?.warning || "Accumulated average exceeds call strike — negative tail risk P&L"}
                                style={{ color: "#856404", cursor: "help", fontWeight: 600 }}
                              >
                                ⚠ Success — Exceeds Target Price
                              </span>
                            )
                            : stock.status}
                          {stock.buildResult?.warning && !stock.buildResult?.derived?.exceeds_target_price && (
                            <span
                              title={stock.buildResult.warning}
                              style={{ marginLeft: 4, color: "#856404", fontSize: 11, cursor: "help" }}
                            >
                              ⚠
                            </span>
                          )}
                        </>
                      )}
                    </td>

                    <td>
                      {stock.status !== "Pending" && stock.buildResult && (
                        <Button
                          size="sm"
                          color="info"
                          onClick={() => {
                            setSelectedResult(stock.buildResult);
                            setSelectedResultStrategyType(strategyType);
                            setDetailsModal(true);
                          }}
                        >
                          Details
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>

            </Table>

            {/* ================= ADVANCED SETTINGS ================= */}
            {settingsSchema?.user_settings && (
              <Card className="mt-3" data-tour="advanced-settings">
                <CardHeader
                  style={{ cursor: "pointer" }}
                  className="d-flex justify-content-between"
                  onClick={() =>
                    setAdvancedOpen(!advancedOpen)
                  }
                >
                  <span>Advanced Settings</span>
                  <span>{advancedOpen ? "▲" : "▼"}</span>
                </CardHeader>

                {advancedOpen && (
                  <CardBody>
                    <Row>
                      {reorderAdjacentSettings(
                        Object.entries(settingsSchema.user_settings),
                        ADVANCED_SETTINGS_ADJACENT_PAIRS
                      ).filter(([key]) => {
                        const alwaysHide = [
                          "tail_risk_pct", "allow_tail_risk", "rollout_mode",
                          "opening_call_delta", "opening_put_delta", "opening_lots",
                        ];
                        if (alwaysHide.includes(key)) return false;
                        if (strategyType === "DELTA_HEDGED_SHORT_STRANGLE") {
                          const dhssObsolete = [
                            "put_max_delta", "put_min_delta", "call_max_delta", "call_min_delta",
                            "threshold_type", "threshold_static_value", "threshold_expression",
                            "delta_hard_imbalance", "price_band_width_pct", "rebalance_realized_pct",
                            "far_otm_max_delta", "far_otm_min_delta", "expiry_week_add_legs_dte_cutoff",
                            "min_realised_ratio", "max_delta_squareoff", "min_target_delta",
                            "max_target_delta", "itm_roll_delta_cutoff",
                          ];
                          if (dhssObsolete.includes(key)) return false;
                        }
                        if (
                          strategyType === "FUTURES_PYRAMID_HEDGING" &&
                          (dynamicValues.hedge_instrument || "SYNTHETIC_STOCK") !== "SYNTHETIC_STOCK"
                        ) {
                          const syntheticOnly = [
                            "synthetic_strike_offset_pct", "synthetic_min_call_delta",
                            "synthetic_max_call_delta", "synthetic_roll_dte_threshold",
                            "put_rollout_dte_threshold", "put_covered_exit_extrinsic_pct",
                          ];
                          if (syntheticOnly.includes(key)) return false;
                        }
                        return true;
                      }).map(([key, config]) => (
                        <Col md={6} key={key}>
                          <FormGroup>
                            <Label>{config.title}</Label>

                            {key === "average_buy_price_to_call_strike_ratio" ? (
                              <div style={{ marginTop: 4, marginBottom: 4 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                                  <small style={{ color: "#4caf50", fontWeight: 600 }}>◀ Bullish</small>
                                  <span style={{ fontWeight: 700, fontSize: "1.05em", color: "#545cd8" }}>
                                    {parseFloat(dynamicValues[key] ?? 1.05).toFixed(2)}
                                  </span>
                                  <small style={{ color: "#e67e22", fontWeight: 600 }}>Bearish ▶</small>
                                </div>
                                <input
                                  type="range"
                                  min="0.85"
                                  max="1.15"
                                  step="0.01"
                                  value={parseFloat(dynamicValues[key] ?? 1.05)}
                                  onChange={(e) =>
                                    setDynamicValues({
                                      ...dynamicValues,
                                      [key]: parseFloat(e.target.value),
                                    })
                                  }
                                  style={{ width: "100%", accentColor: "#545cd8" }}
                                />
                                <div style={{ textAlign: "center", marginTop: 4 }}>
                                  <small style={{
                                    color: parseFloat(dynamicValues[key] ?? 1.05) < 0.98 ? "#4caf50"
                                         : parseFloat(dynamicValues[key] ?? 1.05) > 1.02 ? "#e67e22"
                                         : "#545cd8",
                                  }}>
                                    {parseFloat(dynamicValues[key] ?? 1.05) < 0.98
                                      ? "Bullish — call set above avg cost, more upside potential"
                                      : parseFloat(dynamicValues[key] ?? 1.05) > 1.02
                                      ? "Bearish — tighter call hedge, generates more premium income"
                                      : "Balanced — equal focus on income and upside"}
                                  </small>
                                </div>
                              </div>
                            ) : key === "hedge_instrument" ? (
                              <Input
                                type="select"
                                value={dynamicValues[key] || "SYNTHETIC_STOCK"}
                                onChange={(e) =>
                                  setDynamicValues({ ...dynamicValues, [key]: e.target.value })
                                }
                              >
                                <option value="SYNTHETIC_STOCK">Synthetic Stock (long call + short put)</option>
                                <option value="FUTURES" disabled>Futures (not yet available — no futures broker integrated)</option>
                              </Input>
                            ) : key === "adjustment_type" ? (
                              <Input
                                type="select"
                                value={dynamicValues[key] || "standard"}
                                onChange={(e) =>
                                  setDynamicValues({ ...dynamicValues, [key]: e.target.value })
                                }
                              >
                                <option value="standard">Standard</option>
                                <option value="trim">Trim</option>
                                <option value="roll">Roll</option>
                              </Input>
                            ) : typeof config.value === "boolean" ? (
                              <Input
                                type="checkbox"
                                checked={dynamicValues[key] || false}
                                onChange={(e) =>
                                  setDynamicValues({ ...dynamicValues, [key]: e.target.checked })
                                }
                              />
                            ) : typeof config.value === "string" ? (
                              <Input
                                type="text"
                                value={dynamicValues[key] || ""}
                                onChange={(e) =>
                                  setDynamicValues({ ...dynamicValues, [key]: e.target.value })
                                }
                              />
                            ) : (
                              <Input
                                type="number"
                                value={dynamicValues[key] || ""}
                                onChange={(e) =>
                                  setDynamicValues({ ...dynamicValues, [key]: e.target.value })
                                }
                              />
                            )}

                            <small className="text-muted">
                              {config.detail}
                            </small>
                          </FormGroup>
                        </Col>
                      ))}
                    </Row>
                  </CardBody>
                )}
              </Card>
            )}

            <div className="mt-3" ref={buildButtonRef} data-tour="build-strategy">
            <Button
              color="primary"
              onClick={handleBuildStrategy}
              disabled={loadingPreview}
            >
              {loadingPreview ? (
                <Spinner size="sm" />
              ) : (
                "Build Strategy"
              )}
            </Button>{" "}
            <Button
              color="success"
              onClick={handleSaveStrategy}
              disabled={!previewReady || isSaving}
              data-tour="save-strategy"
            >
              {isSaving ? <Spinner size="sm" /> : "Save Strategy"}
            </Button>
            </div>

          </CardBody>
        </Card>
      )}

    {/* ===================================================== */}
    {/* MODAL — ACTIVATE SAVED STRATEGIES (auto-opens after save) */}
    {/* ===================================================== */}
    <Modal isOpen={activationModalOpen} toggle={() => setActivationModalOpen(false)} size="lg">
      <ModalHeader
        toggle={() => setActivationModalOpen(false)}
        style={{ background: "#f0fff4", borderBottom: "1px solid #b7ebc8" }}
      >
        <span style={{ fontWeight: 700, color: "#155724" }}>Strategy Saved — Activate Now?</span>
      </ModalHeader>
      <ModalBody style={{ padding: "20px 24px" }}>
        <p style={{ fontSize: 13, color: "#555", marginBottom: 16 }}>
          {strategyType === "DELTA_HEDGED_SHORT_STRANGLE"
            ? "Strategy is active — entry trades are queued for the next cycle run."
            : strategyType === "WEEKLY_INDEX_SHORT_STRADDLE"
            ? "Activating doesn't place any trade — the daily OPEN_STRADDLE suggestion appears at the configured open time."
            : "Activating queues the initial SELL_CALL suggestion for review on the next cycle."}
        </p>
        <Table bordered size="sm" style={{ marginBottom: 0 }}>
          <thead style={{ background: "#f8f9fa" }}>
            <tr>
              <th>Symbol</th>
              <th>Options Target</th>
              <th>Lots</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {savedStrategies.map(s => (
              <tr key={s.symbolname}>
                <td style={{ fontWeight: 600 }}>{s.symbolname}</td>
                <td style={{ fontSize: 12, color: "#555" }}>
                  {s.put_symbol ? (
                    <><span style={{ color: "#28a745" }}>CE: </span>{s.call_symbol}<br /><span style={{ color: "#dc3545" }}>PE: </span>{s.put_symbol}</>
                  ) : s.call_symbol}
                </td>
                <td>{s.target_lots ?? "—"}</td>
                <td>
                  {s.error && (
                    <span style={{ color: "#dc3545", fontSize: 11, marginRight: 8 }}>{s.error}</span>
                  )}
                  {s.was_active ? (
                    <span style={{ color: "#0066cc", fontSize: 12 }}>
                      ↻ Settings updated — already active
                    </span>
                  ) : s.activated ? (
                    <span style={{ color: "#28a745", fontSize: 12 }}>✓ Activated</span>
                  ) : (
                    <Button
                      size="sm"
                      color="success"
                      disabled={activatingId === s.strategy_id}
                      onClick={() => handleActivate(s.symbolname, s.strategy_id)}
                    >
                      {activatingId === s.strategy_id ? <Spinner size="sm" /> : "Activate"}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Button color="secondary" size="sm" onClick={() => setActivationModalOpen(false)}>
            Done
          </Button>
        </div>
      </ModalBody>
    </Modal>

    {/* ===================================================== */}
    {/* CARD 4 — CAPITAL PLANNER (collapsible, below activate) */}
    {/* ===================================================== */}
    {previewReady && strategyType === "PYRAMID_HEDGING" && (
      <Card className="mt-3" style={{ border: "1px solid #cce5ff" }}>
        <CardHeader
          style={{ cursor: "pointer", background: "#f0f7ff", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          onClick={() => setCapitalPlannerOpen(o => !o)}
        >
          <span>
            <strong style={{ color: "#0055aa" }}>Capital Planner</strong>
            <span style={{ fontSize: 12, color: "#666", marginLeft: 10 }}>
              How many new Pyramid Hedging trades can you take?
            </span>
          </span>
          <span>{capitalPlannerOpen ? "▲" : "▼"}</span>
        </CardHeader>
        {capitalPlannerOpen && (
          <CapitalPlanner stocks={stocks} />
        )}
      </Card>
    )}

    <Modal
      size="lg"
      isOpen={detailsModal}
      toggle={() =>
        setDetailsModal(false)
      }
      
    >
      <ModalHeader toggle={() =>
        setDetailsModal(false)
      }>
        Strategy Details
      </ModalHeader>

      <ModalBody>

        {!selectedResult && (
          <Alert color="danger">
            Build failed — no result available.
          </Alert>
        )}

        {selectedResult?.error && (
          <Alert color="danger">
            <strong>Build failed:</strong> {selectedResult.error}
            {selectedResult.suggestions?.length > 0 && (
              <ul className="mt-2 mb-0">
                {selectedResult.suggestions.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        {selectedResult?.warning && (
          <Alert color="warning">
            <strong>Note:</strong> {selectedResult.warning}
          </Alert>
        )}

        {/* ── DELTA_HEDGED_SHORT_STRANGLE preview ── */}
        {structure && selectedResultStrategyType === "DELTA_HEDGED_SHORT_STRANGLE" && (
          <>
            {/* Overview tiles */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "16px" }}>
              {[
                ["Expiry",          selectedResult?.intent?.expiry || "—"],
                ["Underlying LTP",  structure.underlying_ltp ? `$${structure.underlying_ltp}` : "—"],
                ["Lots Per Side",   structure.lots_per_side ?? "—"],
                ["Days to Expiry",  structure.days_to_expiry ?? "—"],
                ["Max Profit",      structure.max_profit      ? `$${structure.max_profit}`      : "—"],
                ["Upper Breakeven", structure.call_breakeven  ? `$${structure.call_breakeven}`  : "—"],
                ["Lower Breakeven", structure.put_breakeven   ? `$${structure.put_breakeven}`   : "—"],
                ["Total Premium",   structure.total_premium_per_share ? `$${structure.total_premium_per_share}` : "—"],
                ["Bullish Δ",       structure.put_abs_delta != null && structure.lots_per_side ? (structure.put_abs_delta * structure.lots_per_side).toFixed(4) : "—"],
                ["Bearish Δ",       structure.call_abs_delta != null && structure.lots_per_side ? (structure.call_abs_delta * structure.lots_per_side).toFixed(4) : "—"],
              ].map(([label, val]) => (
                <div key={label} style={{ background: "#f8f9fb", borderRadius: 6, padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, color: "#777" }}>{label}</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{val}</div>
                </div>
              ))}
            </div>

            {/* Active Legs */}
            <div style={{ fontSize: 12, fontWeight: 600, color: "#495057", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Active Legs</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 16 }}>
              <thead>
                <tr style={{ background: "#f0f0f5" }}>
                  <th style={thStyle}>Leg</th>
                  <th style={thStyle}>Symbol</th>
                  <th style={thStyle}>Strike</th>
                  <th style={thStyle}>Lots</th>
                  <th style={thStyle}>Net Delta</th>
                  <th style={thStyle}>Theta</th>
                  <th style={thStyle}>Premium (LTP)</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: "#fff8f0" }}>
                  <td style={tdStyle}><strong style={{ color: "#28a745" }}>CE Short</strong></td>
                  <td style={tdStyle}>{structure.call_symbol ?? "—"}</td>
                  <td style={tdStyle}>{structure.call_strike ? `$${structure.call_strike}` : "—"}</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{structure.lots_per_side ?? "—"}</td>
                  <td style={tdStyle}>{structure.call_abs_delta != null && structure.lots_per_side ? (-structure.call_abs_delta * structure.lots_per_side).toFixed(4) : "—"}</td>
                  <td style={tdStyle}>{structure.call_theta ?? "—"}</td>
                  <td style={tdStyle}><strong>{structure.call_ltp ? `$${structure.call_ltp}` : "—"}</strong></td>
                </tr>
                <tr style={{ background: "#f0f8ff" }}>
                  <td style={tdStyle}><strong style={{ color: "#dc3545" }}>PE Short</strong></td>
                  <td style={tdStyle}>{structure.put_symbol ?? "—"}</td>
                  <td style={tdStyle}>{structure.put_strike ? `$${structure.put_strike}` : "—"}</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{structure.lots_per_side ?? "—"}</td>
                  <td style={tdStyle}>{structure.put_abs_delta != null && structure.lots_per_side ? (structure.put_abs_delta * structure.lots_per_side).toFixed(4) : "—"}</td>
                  <td style={tdStyle}>{structure.put_theta ?? "—"}</td>
                  <td style={tdStyle}><strong>{structure.put_ltp ? `$${structure.put_ltp}` : "—"}</strong></td>
                </tr>
              </tbody>
            </table>

            {/* Delta State */}
            {(structure.delta_difference != null || structure.delta_neutral_threshold != null) && (() => {
              const imb = structure.delta_difference;
              const thr = structure.delta_neutral_threshold;
              const ok  = imb != null && thr != null && imb <= thr;
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: 16 }}>
                  {[
                    ["Delta Imbalance",  imb != null ? imb.toFixed(4) : "—"],
                    ["Delta Neutral",    imb == null ? "—" : <span style={{ fontWeight: 700, color: ok ? "#28a745" : "#dc3545" }}>{ok ? "Yes" : "No"}</span>],
                    ["Threshold",        thr != null ? thr.toFixed(4) : "—"],
                    ["Threshold Type",   selectedResult?.intent?.settings_snapshot?.threshold_type ?? "—"],
                  ].map(([label, val]) => (
                    <div key={label} style={{ background: "#f8f9fb", borderRadius: 6, padding: "10px 12px" }}>
                      <div style={{ fontSize: 11, color: "#777" }}>{label}</div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{val}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            <PayoffChart
              strategyType="DELTA_HEDGED_SHORT_STRANGLE"
              derived={structure}
              reference={selectedResult?.reference}
              intent={selectedResult?.intent}
              title="Short Strangle Payoff at Expiry"
            />
          </>
        )}

        {/* ── WEEKLY_INDEX_SHORT_STRADDLE preview ── */}
        {structure && selectedResultStrategyType === "WEEKLY_INDEX_SHORT_STRADDLE" && (
          <div style={{ border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", background: "#fafafa" }}>
            <h5 style={{ marginBottom: "12px" }}>Today's Preview</h5>
            <Alert color="info" style={{ fontSize: 13 }}>
              Strikes shown are a live preview only — the actual strikes are resolved fresh
              every trading day at the configured open time, not locked in now.
            </Alert>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px" }}>
              {[
                ["Underlying LTP", structure.underlying_ltp ? `$${structure.underlying_ltp}` : "—"],
                ["Nearest Weekly Expiry", structure.preview_expiry || "—"],
                ["Preview Call Strike", structure.preview_call_strike ? `$${structure.preview_call_strike}` : "—"],
                ["Preview Put Strike", structure.preview_put_strike ? `$${structure.preview_put_strike}` : "—"],
                ["Call/Put Pairs", structure.pairs ?? "—"],
              ].map(([label, val]) => (
                <div key={label} style={{ background: "#fff", borderRadius: 6, padding: "10px 12px", border: "1px solid #eee" }}>
                  <div style={{ fontSize: 11, color: "#777" }}>{label}</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{val}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── PYRAMID_HEDGING preview ── */}
        {structure && selectedResultStrategyType !== "DELTA_HEDGED_SHORT_STRANGLE"
          && selectedResultStrategyType !== "WEEKLY_INDEX_SHORT_STRADDLE" && (
          <>
            <div style={{ border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", marginBottom: "20px", background: "#fafafa" }}>
              <h5 style={{ marginBottom: "12px" }}>Strategy Structure</h5>

              {/* ── Call leg ── */}
              <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>
                {structure.opening_strangle_mode ? "Opening Call (Short, Far-OTM)" : "Call (Short)"}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", rowGap: "10px", marginBottom: 16 }}>
                <div><strong>Call Symbol</strong><br/>{structure.call_symbol ?? "—"}</div>
                <div><strong>Strike</strong><br/>{structure.call_strike != null ? `$${structure.call_strike}` : "—"}</div>
                <div><strong>Call LTP</strong><br/>{structure.call_ltp != null ? `$${structure.call_ltp}` : "—"}</div>
                <div><strong>Call Delta</strong><br/>{structure.call_delta != null ? structure.call_delta.toFixed(3) : "—"}</div>
                {!structure.opening_strangle_mode && (
                  <div><strong>Initial Hedge %</strong><br/>{structure.hedge_percent != null ? structure.hedge_percent.toFixed(1) + "%" : "—"}</div>
                )}
                <div><strong>Call Strike Ratio</strong><br/>{structure.achieved_ratio ?? "—"}</div>
              </div>

              {/* ── Put leg (CSP / opening strangle put) — only when enabled ── */}
              {structure.csp_enabled && (
                <>
                  <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 6, fontWeight: 600, borderTop: "1px solid #eee", paddingTop: 12 }}>
                    {structure.opening_strangle_mode ? "Opening Put (Short, Far-OTM)" : "Put (CSP — Cash Secured)"}
                  </div>
                  {structure.csp_error ? (
                    <div style={{ color: "#dc3545", fontSize: 13 }}>{structure.csp_error}</div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", rowGap: "10px", marginBottom: 16 }}>
                      <div><strong>Put Symbol</strong><br/>{structure.csp_symbol ?? "—"}</div>
                      <div><strong>Put Strike</strong><br/>{structure.put_strike != null ? `$${structure.put_strike}` : "—"}</div>
                      <div><strong>Put LTP</strong><br/>{structure.put_ltp != null ? `$${structure.put_ltp}` : "—"}</div>
                      <div><strong>Put Delta</strong><br/>{structure.put_delta != null ? structure.put_delta.toFixed(3) : "—"}</div>
                      <div><strong>Distance from LTP</strong><br/>{structure.put_distance_pct != null ? structure.put_distance_pct.toFixed(1) + "%" : "—"}</div>
                    </div>
                  )}
                </>
              )}

              {/* ── Common fields ── */}
              <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 6, fontWeight: 600, borderTop: "1px solid #eee", paddingTop: 12 }}>Position</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", rowGap: "10px" }}>
                <div><strong>Reference Avg Price</strong><br/>{structure.reference_avg_price != null ? `$${structure.reference_avg_price}` : "—"}</div>
                <div><strong>Lot Size</strong><br/>{structure.lot_size ?? "—"}</div>
                <div><strong>Target Lots</strong><br/>{structure.target_lots ?? "—"}</div>
                <div><strong>Next Buy Price</strong><br/>{structure.next_buy_price != null ? `$${structure.next_buy_price}` : "—"}</div>
                <div><strong>Next Buy Qty</strong><br/>{structure.next_buy_quantity ?? "—"}</div>
              </div>

              {/* ── Tail P&L at completion vs. Min Profit (CSM) — the actual
                   floor the ladder is built to clear, marked to the call's
                   strike at full completion. Distinct from the per-rung
                   mark-to-market columns in the ladder table below. ── */}
              {structure.tail_risk_pnl != null && (() => {
                const minCsm = selectedResult?.intent?.settings_snapshot?.min_profit_csm;
                const clears = minCsm != null ? structure.tail_risk_pnl >= minCsm : null;
                return (
                  <div style={{
                    marginTop: 14, padding: "10px 12px", borderRadius: 6,
                    border: `1px solid ${clears === false ? "#f5c6cb" : "#c3e6cb"}`,
                    background: clears === false ? "#f8d7da" : "#d4edda",
                  }}>
                    <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", fontWeight: 600 }}>
                      {selectedResultStrategyType === "FUTURES_PYRAMID_HEDGING"
                        ? "Tail P&L at Completion (at ladder completion price)"
                        : "Tail P&L at Completion (marked to call strike)"}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: clears === false ? "#721c24" : "#155724" }}>
                      {fmtPnl(structure.tail_risk_pnl)}
                      {minCsm != null && (
                        <span style={{ fontSize: 12, fontWeight: 500, marginLeft: 8 }}>
                          {clears ? "✓ clears" : "✗ below"} Min Profit (CSM) target of {fmtPnl(minCsm)}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>
                      {selectedResultStrategyType === "FUTURES_PYRAMID_HEDGING"
                        ? "Matches the last ladder row below: futures + call are capped flat at the strike once fully hedged, but any netted existing stock isn't covered by the call, so it's marked at this ladder's own completion price instead of the strike."
                        : "This is the number the ladder is built to guarantee — the per-rung P&L columns below are illustrative mark-to-market at each rung's own price, not this figure."}
                    </div>
                  </div>
                );
              })()}
            </div>

            <PayoffChart
              strategyType="PYRAMID_HEDGING"
              derived={structure}
              title="Pyramid Hedging Payoff at Expiry"
            />

            {structure.ladder?.length > 0 ? (
              <LadderTable structure={structure} />
            ) : structure.opening_strangle_mode ? (
              <Alert color="primary">
                <strong>Opening Strangle Mode</strong><br/>
                No stock position — strategy opens with <strong>{structure.target_lots} lot(s)</strong> of far-OTM call{structure.csp_enabled && !structure.csp_error ? " and put" : ""}. Stock accumulation will begin after the first buy trigger fires.
              </Alert>
            ) : structure.covered_call_mode ? (
              <Alert color="success">
                <strong>Traditional Covered Call</strong><br/>
                Position is fully invested at {structure.achievable_lots} lot(s) ({selectedResult?.runtime?.current_netqty} shares at avg ${structure.reference_avg_price}).
                Sell <strong>{structure.target_lots} call lot(s)</strong> of {structure.call_symbol} to set up a covered call. No additional stock buying required.
              </Alert>
            ) : (
              <Alert color="info">
                Current position ({selectedResult?.runtime?.current_netqty} shares at avg ${structure.reference_avg_price}) already covers the {structure.target_lots}-lot target ({structure.target_lots * structure.lot_size} shares). No additional buying required.
              </Alert>
            )}
          </>
        )}

      </ModalBody>
    </Modal>

    </>
  );
};

/* ── Ladder table with per-step P&L ── */
const fmtPnl = (v) => {
  if (v == null) return "—";
  const sign = v >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(Math.round(v)).toLocaleString("en-US")}`;
};

const LadderTable = ({ structure }) => {
  const callQty    = (structure.target_lots || 1) * (structure.lot_size || 1);
  const callLtp    = structure.call_ltp    || 0;
  const callStrike = structure.call_strike || 0;
  const ltp        = structure.underlying_ltp || structure.reference_avg_price || 0;

  // CSP put fields (only used when csp_enabled and no error)
  const hasCsp     = structure.csp_enabled && !structure.csp_error;
  const putLtp     = hasCsp ? (structure.put_ltp    || 0) : 0;
  const putStrike  = hasCsp ? (structure.put_strike || 0) : 0;
  const putQty     = hasCsp ? (structure.lot_size   || 1) : 0;   // 1 lot of CSP put

  // Options P&L at expiry: premium received when the call (and put) expires worthless.
  const optionsPnlBase = callLtp * callQty + putLtp * putQty;

  return (
    <>
      <Table bordered style={{ marginTop: 16, fontSize: 13 }}>
        <thead>
          <tr>
            <th>Step</th>
            <th>Buy Price</th>
            <th style={{ color: "#888" }}>% from LTP</th>
            <th>Qty</th>
            <th>Investment</th>
            <th>Total Qty</th>
            <th>Avg Price</th>
            <th style={{ color: "#545cd8", whiteSpace: "nowrap" }}>Est. Hedge %†</th>
            <th style={{ color: "#6f42c1" }}>Position P&L‡</th>
            <th style={{ color: "#17a2b8" }}>Options P&L‡</th>
          </tr>
        </thead>
        <tbody>
          {structure.ladder.map(row => {
            const pctFromLtp    = ltp > 0 ? ((row.buy_price - ltp) / ltp * 100) : null;
            // P&L at each step's price level: unrealized stock gain + options mark-to-market
            const stockPnl      = Math.round((row.buy_price - row.avg_price) * row.total_qty);
            const callIntrinsic = callStrike > 0 ? Math.max(0, row.buy_price - callStrike) * callQty : 0;
            const optionsPnl    = Math.round(optionsPnlBase - callIntrinsic);
            const hedgePct      = row.projected_hedge_pct != null ? row.projected_hedge_pct : null;
            return (
              <tr key={row.step}>
                <td>{row.step}</td>
                <td>${row.buy_price}</td>
                <td style={{ color: pctFromLtp >= 0 ? "#28a745" : "#dc3545", fontSize: 12 }}>
                  {pctFromLtp != null ? `${pctFromLtp >= 0 ? "+" : ""}${pctFromLtp.toFixed(1)}%` : "—"}
                </td>
                <td>{row.qty}</td>
                <td style={{ color: "#555" }}>${row.investment != null ? Math.round(row.investment).toLocaleString("en-US") : "—"}</td>
                <td>{row.total_qty}</td>
                <td>${row.avg_price}</td>
                <td style={{ color: "#545cd8", fontWeight: 600 }}>
                  {hedgePct != null ? `~${hedgePct}%` : "—"}
                </td>
                <td style={{ color: stockPnl >= 0 ? "#28a745" : "#dc3545", fontWeight: 600 }}>
                  {fmtPnl(stockPnl)}
                </td>
                <td style={{ color: optionsPnl >= 0 ? "#28a745" : "#dc3545" }}>
                  {fmtPnl(optionsPnl)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <div style={{ fontSize: 11, color: "#888", marginTop: 4, fontStyle: "italic" }}>
        † Est. Hedge % is a forward projection of delta-weighted hedge when the stock reaches each ladder step. For illustration purposes only. Actual hedge may vary with time to expiry and implied volatility.<br/>
        ‡ Illustrative mark-to-market <strong>if the stock were sitting at that rung's own price right now</strong> — not the strategy's projected outcome. Stock P&L = unrealized gain on accumulated shares at that price. Options P&L = premium collected minus the short call's intrinsic loss once price exceeds the call strike (ITM). For the actual profit floor this ladder is built to guarantee at full completion, see "Tail P&L at Completion" above.
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────────────────────
   CAPITAL PLANNER
   Helps decide how many new Pyramid Hedging trades to initiate
   based on available capital and step-coverage preference.
   ───────────────────────────────────────────────────────────── */
const fmtL = (n) => {
  if (n == null || isNaN(n)) return "$0";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000)    return `$${(n / 1000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString("en-US")}`;
};

const CapitalPlanner = ({ stocks }) => {
  const [availableCapital, setAvailableCapital] = React.useState("");
  const [reserveSteps, setReserveSteps] = React.useState(1);

  // Only use successfully built PYRAMID strategies with a ladder
  const builtStocks = stocks.filter(
    s => s.selected && s.status === "Success" &&
         s.buildResult?.derived?.ladder?.length > 0
  );
  if (builtStocks.length === 0) return null;

  // ── Per-strategy capital breakdown ──────────────────────────
  const breakdowns = builtStocks.map(stock => {
    const derived  = stock.buildResult.derived;
    const ladder   = derived.ladder || [];
    const ltp      = derived.underlying_ltp || stock.underlying_ltp || 0;

    // Initial position the user already holds (or would buy to initiate)
    const initialQty   = stock.stock?.quantity  || 0;
    const initialPrice = stock.stock?.avg_price || ltp;
    const initialCap   = Math.round(initialQty * initialPrice);

    // Each future ladder step: qty × buy_price
    const steps = ladder.map(s => ({
      step:       s.step,
      capital:    Math.round((s.qty || 0) * (s.buy_price || 0)),
      buyPrice:   s.buy_price,
      pctFromLtp: ltp > 0
        ? ((s.buy_price - ltp) / ltp * 100)
        : null,
    }));

    const ladderTotal = steps.reduce((sum, s) => sum + s.capital, 0);

    return {
      stockName:   stock.stock_name,
      lotSize:     derived.lot_size   || 1,
      targetLots:  derived.target_lots || 1,
      initialCap,
      steps,
      ladderTotal,
      fullCapital: initialCap + ladderTotal,
    };
  });

  const maxSteps = Math.max(...breakdowns.map(bd => bd.steps.length), 0);

  // Capital committed per trade at current slider position
  const capitalPerTrade = (bd) => {
    const reserve = bd.steps
      .slice(0, reserveSteps)
      .reduce((sum, s) => sum + s.capital, 0);
    return bd.initialCap + reserve;
  };

  const capital = parseFloat((availableCapital || "").replace(/,/g, "")) || 0;

  // Average across all shown strategies (if multiple)
  const avgCpt = breakdowns.length > 0
    ? breakdowns.reduce((sum, bd) => sum + capitalPerTrade(bd), 0) / breakdowns.length
    : 0;

  const numTrades     = capital > 0 && avgCpt > 0 ? Math.floor(capital / avgCpt) : 0;
  const deployedCap   = numTrades * avgCpt;
  const remainingCap  = capital - deployedCap;
  const utilisation   = capital > 0 ? ((deployedCap / capital) * 100).toFixed(0) : 0;

  // Full-coverage comparison (for footer note)
  const avgFullCpt = breakdowns.reduce((sum, bd) => sum + bd.fullCapital, 0) / breakdowns.length;
  const tradesAtFull = capital > 0 && avgFullCpt > 0 ? Math.floor(capital / avgFullCpt) : 0;

  // Probability heuristic labels based on % move required
  const stepLabel = (pct) => {
    const p = Math.abs(pct);
    if (p < 3)  return "~60% likely";
    if (p < 6)  return "~40% likely";
    if (p < 10) return "~25% likely";
    if (p < 15) return "~12% likely";
    return "~5% likely";
  };

  const sliderLabel = () => {
    if (reserveSteps === 0) return "No reserve — initial tranche only (most aggressive)";
    if (reserveSteps === maxSteps) return `Full coverage — all ${maxSteps} ladder steps (most conservative)`;
    return `Reserve for ${reserveSteps} ladder step${reserveSteps > 1 ? "s" : ""} beyond current position`;
  };

  return (
    <CardBody>

        {/* ── Inputs ── */}
        <Row className="mb-3 align-items-end">
          <Col md={4}>
            <FormGroup className="mb-0">
              <Label style={{ fontWeight: 600 }}>Available Capital ($)</Label>
              <Input
                type="number"
                placeholder="e.g. 1000000"
                value={availableCapital}
                onChange={e => setAvailableCapital(e.target.value)}
              />
              {capital > 0 && (
                <small style={{ color: "#666" }}>{fmtL(capital)}</small>
              )}
            </FormGroup>
          </Col>
          <Col md={7}>
            <Label style={{ fontWeight: 600 }}>
              Reserve depth: {reserveSteps === 0 ? "initial tranche only" : `${reserveSteps} ladder step${reserveSteps > 1 ? "s" : ""}`}
            </Label>
            <Input
              type="range"
              min={0}
              max={maxSteps}
              step={1}
              value={reserveSteps}
              onChange={e => setReserveSteps(Number(e.target.value))}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#888" }}>
              <span>Aggressive<br/>(fewer reserves)</span>
              <span style={{ textAlign: "center" }}>{sliderLabel()}</span>
              <span style={{ textAlign: "right" }}>Conservative<br/>(full coverage)</span>
            </div>
          </Col>
        </Row>

        {/* ── Per-strategy breakdown table ── */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 16 }}>
            <thead>
              <tr style={{ background: "#f5f7fa" }}>
                <th style={cPlanTh}>Stock</th>
                <th style={cPlanTh}>Target</th>
                <th style={cPlanTh}>Current Position</th>
                {Array.from({ length: maxSteps }, (_, i) => (
                  <th key={i} style={{
                    ...cPlanTh,
                    color: i < reserveSteps ? "#0066cc" : "#bbb",
                    background: i < reserveSteps ? "#eef5ff" : undefined,
                  }}>
                    {i < reserveSteps ? "✓ " : ""}Step {i + 1}
                  </th>
                ))}
                <th style={{ ...cPlanTh, color: "#dc3545" }}>Full Lot</th>
                <th style={{ ...cPlanTh, background: "#e8f4e8", color: "#1a7a1a" }}>
                  At Selection
                </th>
              </tr>
            </thead>
            <tbody>
              {breakdowns.map(bd => (
                <tr key={bd.stockName}>
                  <td style={cPlanTd}><strong>{bd.stockName}</strong></td>
                  <td style={cPlanTd}>{bd.targetLots} lots × {bd.lotSize}</td>
                  <td style={cPlanTd}>
                    {fmtL(bd.initialCap)}
                    <div style={{ fontSize: 10, color: "#888" }}>already held</div>
                  </td>
                  {Array.from({ length: maxSteps }, (_, i) => {
                    const step = bd.steps[i];
                    return (
                      <td key={i} style={{
                        ...cPlanTd,
                        color: i < reserveSteps ? "#333" : "#bbb",
                        background: i < reserveSteps ? "#f7fbff" : undefined,
                      }}>
                        {step ? (
                          <>
                            {fmtL(step.capital)}
                            {step.pctFromLtp != null && (
                              <div style={{ fontSize: 10, color: "#888" }}>
                                +{step.pctFromLtp.toFixed(1)}% — {stepLabel(step.pctFromLtp)}
                              </div>
                            )}
                          </>
                        ) : "—"}
                      </td>
                    );
                  })}
                  <td style={{ ...cPlanTd, color: "#dc3545", fontWeight: 600 }}>
                    {fmtL(bd.fullCapital)}
                  </td>
                  <td style={{ ...cPlanTd, background: "#e8f4e8", fontWeight: 700, color: "#1a7a1a" }}>
                    {fmtL(capitalPerTrade(bd))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Recommendation panel ── */}
        {capital > 0 && avgCpt > 0 ? (
          <div style={{
            background: "linear-gradient(135deg, #0055aa11 0%, #00aa5511 100%)",
            border: "1px solid #0066cc44",
            borderRadius: 10,
            padding: "16px 20px",
          }}>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 8 }}>
              With <strong>{fmtL(capital)}</strong> available capital, reserving for{" "}
              <strong>{reserveSteps === 0 ? "no additional steps" : `${reserveSteps} ladder step${reserveSteps > 1 ? "s" : ""}`}</strong>:
            </div>

            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 12 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: "#0055aa" }}>{numTrades}</div>
                <div style={{ fontSize: 12, color: "#666" }}>trades you can take</div>
              </div>
              <div style={{ borderLeft: "1px solid #ccc", paddingLeft: 24, display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 11, color: "#888" }}>Capital per trade</div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{fmtL(Math.round(avgCpt))}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#888" }}>Total committed</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#cc3300" }}>{fmtL(Math.round(deployedCap))}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#888" }}>Buffer kept free</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#007733" }}>{fmtL(Math.round(remainingCap))}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#888" }}>Capital utilisation</div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{utilisation}%</div>
                </div>
              </div>
            </div>

            {/* Interpretation note */}
            <div style={{
              fontSize: 12, color: "#444", borderTop: "1px solid #ccc",
              paddingTop: 10, lineHeight: 1.6
            }}>
              <strong>How to read this:</strong> Each ladder step is triggered only when the stock
              rises by the corresponding % shown above. In a calendar month, a &gt;15% move is
              rare (~5–10% probability). By reserving for fewer steps, you free up capital for
              more trades — but if multiple stocks move sharply, you'll need to top up or trim.
              {reserveSteps < maxSteps && tradesAtFull > 0 && (
                <> At <em>full coverage</em> (worst case), you could only take{" "}
                <strong>{tradesAtFull} trade{tradesAtFull !== 1 ? "s" : ""}</strong> with the same capital.</>
              )}
              {reserveSteps === 0 && (
                <> <strong style={{ color: "#cc6600" }}>Warning:</strong> Zero reserve means you'd need
                to find additional capital if any stock rises to its first ladder step.</>
              )}
            </div>
          </div>
        ) : (
          <div style={{ color: "#888", fontSize: 13, padding: "12px 0" }}>
            Enter your available capital above to see how many trades you can take.
          </div>
        )}
      </CardBody>
  );
};

const cPlanTh = {
  padding: "8px 10px", fontWeight: 600, fontSize: 12,
  borderBottom: "2px solid #dee2e6", textAlign: "left",
};
const cPlanTd = {
  padding: "8px 10px", fontSize: 13,
  borderBottom: "1px solid #f0f0f0", verticalAlign: "top",
};

const thStyle = {
  padding: "8px 10px",
  textAlign: "left",
  borderBottom: "1px solid #e0e0e0",
  fontWeight: 600,
  fontSize: 12,
  color: "#444",
};

const tdStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid #f0f0f0",
  fontSize: 13,
};

export default StrategyBuild;
